import type {
  CreatePartnerInput,
  PartnerComplianceDoc,
  PartnerProfile,
  PublicUser,
  RequestComplianceUploadInput,
  ReviewComplianceDocInput,
  UpsertPartnerProfileInput,
} from '@bcl/shared';
import { api } from '@/lib/api';

export type PartnerWithProfile = PublicUser & { profile: PartnerProfile | null };

export type PartnerDetail = {
  user: PublicUser;
  profile: PartnerProfile | null;
  complianceDocs: PartnerComplianceDoc[];
};

export type MyPartnerProfile = {
  profile: PartnerProfile | null;
  complianceDocs: PartnerComplianceDoc[];
};

export type ComplianceUploadResponse = {
  document: PartnerComplianceDoc;
  uploadUrl: string;
};

export type ComplianceReminderResult = {
  remindersSent: number;
  expiredMarked: number;
};

// --- Espace partenaire ---

export async function getMyPartnerProfile(): Promise<MyPartnerProfile> {
  const { data } = await api.get<MyPartnerProfile>('/partners/me/profile');
  return data;
}

export async function upsertMyPartnerProfile(
  input: UpsertPartnerProfileInput,
): Promise<PartnerProfile> {
  const { data } = await api.put<PartnerProfile>('/partners/me/profile', input);
  return data;
}

export async function requestComplianceUpload(
  input: RequestComplianceUploadInput,
): Promise<ComplianceUploadResponse> {
  const { data } = await api.post<ComplianceUploadResponse>(
    '/partners/me/compliance/upload-url',
    input,
  );
  return data;
}

export async function getMyComplianceDownloadUrl(
  documentId: string,
): Promise<string> {
  const { data } = await api.get<{ url: string }>(
    `/partners/me/compliance/${documentId}/download-url`,
  );
  return data.url;
}

// --- Back-office (partners.manage) ---

export async function listPartners(): Promise<PartnerWithProfile[]> {
  const { data } = await api.get<PartnerWithProfile[]>('/partners');
  return data;
}

export async function onboardPartner(
  input: CreatePartnerInput,
): Promise<{ user: PublicUser; profile: PartnerProfile }> {
  const { data } = await api.post<{ user: PublicUser; profile: PartnerProfile }>(
    '/partners',
    input,
  );
  return data;
}

export async function getPartnerDetail(
  partnerId: string,
): Promise<PartnerDetail> {
  const { data } = await api.get<PartnerDetail>(`/partners/${partnerId}`);
  return data;
}

export async function getComplianceDownloadUrl(
  documentId: string,
): Promise<string> {
  const { data } = await api.get<{ url: string }>(
    `/partners/compliance/${documentId}/download-url`,
  );
  return data.url;
}

export async function reviewComplianceDoc(
  documentId: string,
  input: ReviewComplianceDocInput,
): Promise<PartnerComplianceDoc> {
  const { data } = await api.patch<PartnerComplianceDoc>(
    `/partners/compliance/${documentId}/review`,
    input,
  );
  return data;
}

export async function runComplianceReminders(): Promise<ComplianceReminderResult> {
  const { data } = await api.post<ComplianceReminderResult>(
    '/partners/run-compliance-reminders',
  );
  return data;
}
